/**
 * Dashboards API endpoints
 */
import type {
 ApiResponse,
 ApiListResponse,
 Dashboard,
 DashboardInput,
 DashboardUpdate,
 DashboardItem,
 DashboardItemInput,
 DashboardRenderRequest,
 Chart,
 Report,
 ReportQueryResult,
} from "../lib/types";
import { authenticatedRequest } from "../lib/api";

export async function getDashboards(token: string): Promise<Dashboard[]> {
 return authenticatedRequest<ApiListResponse<Dashboard>>(
  "/api/dashboards",
  token
 ).then((res) => res.data);
}

export async function getDashboard(
 token: string,
 id: string
): Promise<Dashboard & { items: DashboardItem[] }> {
 return authenticatedRequest<
  ApiResponse<{ dashboard: Dashboard; items: DashboardItem[] }>
 >(`/api/dashboards/${id}`, token).then((res) => {
  // Backend returns { dashboard: {...}, items: [...] }, flatten it
  const data = res.data as { dashboard: Dashboard; items: DashboardItem[] };
  return {
   ...data.dashboard,
   items: data.items,
  };
 });
}

export async function createDashboard(
 token: string,
 input: DashboardInput
): Promise<Dashboard> {
 return authenticatedRequest<ApiResponse<Dashboard>>("/api/dashboards", token, {
  method: "POST",
  body: JSON.stringify(input),
 }).then((res) => res.data);
}

export async function updateDashboard(
 token: string,
 id: string,
 input: DashboardUpdate
): Promise<Dashboard> {
 return authenticatedRequest<ApiResponse<Dashboard>>(
  `/api/dashboards/${id}`,
  token,
  {
   method: "PUT",
   body: JSON.stringify(input),
  }
 ).then((res) => res.data);
}

export async function deleteDashboard(
 token: string,
 id: string
): Promise<void> {
 return authenticatedRequest<void>(`/api/dashboards/${id}`, token, {
  method: "DELETE",
 });
}

export async function addDashboardItem(
 token: string,
 dashboardId: string,
 input: DashboardItemInput
): Promise<DashboardItem> {
 return authenticatedRequest<ApiResponse<DashboardItem>>(
  `/api/dashboards/${dashboardId}/items`,
  token,
  {
   method: "POST",
   body: JSON.stringify(input),
  }
 ).then((res) => res.data);
}

export async function updateDashboardItem(
 token: string,
 dashboardId: string,
 itemId: string,
 input: DashboardItemInput
): Promise<DashboardItem> {
 return authenticatedRequest<ApiResponse<DashboardItem>>(
  `/api/dashboards/${dashboardId}/items/${itemId}`,
  token,
  {
   method: "PUT",
   body: JSON.stringify(input),
  }
 ).then((res) => res.data);
}

export async function removeDashboardItem(
 token: string,
 dashboardId: string,
 itemId: string
): Promise<void> {
 return authenticatedRequest<void>(
  `/api/dashboards/${dashboardId}/items/${itemId}`,
  token,
  {
   method: "DELETE",
  }
 );
}

export async function renderDashboard(
 token: string,
 id: string,
 request?: DashboardRenderRequest
): Promise<{
 dashboard: Dashboard;
 items: Array<
  DashboardItem & {
   chart: Chart | null;
   report: Report | null;
   data: ReportQueryResult;
  }
 >;
}> {
 return authenticatedRequest<
  ApiResponse<{
   dashboard: Dashboard;
   items: Array<
    DashboardItem & {
     chart: Chart | null;
     report: Report | null;
     data: ReportQueryResult;
    }
   >;
  }>
 >(`/api/dashboards/${id}/render`, token, {
  method: "POST",
  body: JSON.stringify(request || {}),
 }).then((res) => res.data);
}
